import { useState } from "react";
import type { GeneratedDefinition, GeneratedStatus } from "@keydials/api-client";
import { recolourStatus, removeStatus, renameStatus, setCountsAsDone } from "@keydials/dial-workspace";
import { addNamedState, colourInputValue } from "./coloursView";
import { ConfirmRemove } from "./ConfirmRemove";
import { builderCopy as copy, fill } from "./copy";

interface StatusCustomiserProps {
  statuses: GeneratedStatus[];
  disabled: boolean;
  edit: (change: (model: GeneratedDefinition) => GeneratedDefinition) => void;
}

/**
 * "Customise" on the Colours stage (KEY-560): each state's colour, name and whether it counts as done, and adding or removing
 * states. The last state can't be removed, and a colour the platform has locked can't be changed. A new state is added once it
 * has a name.
 */
export function StatusCustomiser({ statuses, disabled, edit }: StatusCustomiserProps) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const [newName, setNewName] = useState("");

  function add() {
    if (!newName.trim()) return;
    const name = newName;
    edit((model) => addNamedState(model, name)?.model ?? model);
    setNewName("");
  }

  return (
    <div className="kd-states">
      <ol className="kd-states-list">
        {statuses.map((status) => (
          <li key={status.key} className="kd-state">
            <div className="kd-state-row">
              <input
                type="color"
                className="kd-state-colour"
                aria-label={fill(copy.colours.colourLabel, { name: status.name || copy.build.untitled })}
                value={colourInputValue(status.colour)}
                disabled={disabled || status.colourLocked === true}
                onChange={(e) => edit((model) => recolourStatus(model, status.key, e.target.value))}
              />
              <input
                className="kd-state-name"
                aria-label={copy.build.nameLabel}
                value={status.name}
                disabled={disabled}
                onChange={(e) => edit((model) => renameStatus(model, status.key, e.target.value))}
              />
              <button type="button" className="kd-icon-btn" disabled={disabled || statuses.length <= 1} onClick={() => setConfirming(status.key)}>
                {copy.colours.remove}
              </button>
            </div>
            <label className="kd-state-done">
              <input
                type="checkbox"
                role="switch"
                checked={status.isTerminal}
                disabled={disabled}
                onChange={(e) => edit((model) => setCountsAsDone(model, status.key, e.target.checked))}
              />
              {copy.colours.countsAsDone}
            </label>
            {confirming === status.key && (
              <ConfirmRemove
                name={status.name}
                onKeep={() => setConfirming(null)}
                onRemove={() => {
                  setConfirming(null);
                  edit((model) => removeStatus(model, status.key));
                }}
              />
            )}
          </li>
        ))}
      </ol>
      <p className="kd-builder-muted">{copy.colours.countsAsDoneHint}</p>
      <form
        className="kd-state-add"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <input aria-label={copy.build.nameLabel} value={newName} disabled={disabled} onChange={(e) => setNewName(e.target.value)} />
        <button type="submit" className="kd-btn" disabled={disabled || !newName.trim()}>
          {copy.colours.addState}
        </button>
      </form>
    </div>
  );
}
